import React, { useEffect, useMemo } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import { setPatientId } from '../../store/actions/patientData';
import { patientInstance } from '../../settings/axios';
import api from '../../settings/api';
import toast from '../../helpers/toast';
import { getCookie } from '../../helpers/cookies';

const AutoLogin = (props) => {
	const location = useLocation();
	const history = useHistory();
	const { setPatientId } = props;

	// Parse URL params only once per location.search
	const params = useMemo(
		() => new URLSearchParams(location.search),
		[location.search],
	);
	const samlUrl = params.get('saml');
	const redirect = params.get('redirect');
	const data = params.get('data');
	const relay = params.get('relay');

	// Memoize parsed relay to avoid repeated parsing work
	const parsedRelay = useMemo(() => {
		// Helper: Parse RelayState from either base64 JSON or URL-style path
		const parseRelay = (relayValue) => {
			if (!relayValue) return null;
			// Try URL-style relay: "/PatientDetails?data=123"
			try {
				if (relayValue.startsWith('/')) {
					const u = new URL(relayValue, window.location.origin);
					return {
						redirect: u.pathname.replace(/^\//, ''),
						data: u.searchParams.get('data'),
					};
				}
			} catch (_) {
				// URL-style parse failed; will attempt base64 JSON next
			}
			// Try base64-encoded JSON: eyJyZWRpcmVjdCI6ICJQYXRpZW50RGV0YWlscyIsICJkYXRhIjogIjk3MDE3In0=
			try {
				const decoded = atob(relayValue);
				const obj = JSON.parse(decoded);
				if (obj && typeof obj === 'object') {
					return { redirect: obj.redirect, data: obj.data };
				}
			} catch (_) {
				// Base64 decode/JSON parse failed; will attempt plain JSON next
			}
			// Try plain JSON (non-base64), for completeness/backward-compat
			try {
				const obj = JSON.parse(relayValue);
				if (obj && typeof obj === 'object') {
					return { redirect: obj.redirect, data: obj.data };
				}
			} catch (_) {
				// Plain JSON parse failed; all strategies exhausted
			}
			return null;
		};

		return parseRelay(relay);
	}, [relay]);

	useEffect(() => {
		// Prefer URL-style RelayState for ACS; keep simple and readable
		let relayState = '';
		if (redirect && data) {
			relayState = `/${redirect}?data=${encodeURIComponent(data)}`;
		}
		if (samlUrl) {
			try {
				// Append RelayState param to SAML URL
				const url = new URL(samlUrl);
				if (url.protocol !== 'http:' && url.protocol !== 'https:') {
					toast.error('Invalid SSO URL.');
					return;
				}
				if (relayState) {
					url.searchParams.set('RelayState', relayState);
				}

				window.location.replace(url.toString());
			} catch (e) {
				toast.error('Invalid SSO URL.');
			}
		}
	}, [samlUrl, redirect, data]);

	useEffect(() => {
		if (!parsedRelay) return;
		if (parsedRelay.redirect === 'PatientDetails' && parsedRelay.data) {
			const run = async () => {
				// Step 1: Get access token from cookie
				const accessToken = getCookie('access_token');
				if (!accessToken) {
					toast.error('No access token found. Please login again.');
					return;
				}
				try {
					const res = await patientInstance.get(
						`${api.patient_details.get_patient_by_sf_id}/${parsedRelay.data}`,
						{
							headers: {
								Authorization: `Bearer ${accessToken}`,
								Accept: 'application/json',
							},
						},
					);
					const data = res.data;
					if (data && data.id) {
						setPatientId({
							patientId: data.id,
							errorId: null,
							cp_id: data.concentrator_profile_id,
						});
						history.replace('/patient_detail');
					} else {
						toast.warn('Patient not found for this Salesforce ID');
						history.replace('/dashboard');
					}
				} catch (error) {
					if (error.response && error.response.status === 404) {
						toast.warn('Patient not found for this Salesforce ID');
					}
					history.replace('/dashboard');
				}
			};
			run();
		}
	}, [parsedRelay, setPatientId, history]);

	return (
		<div style={{ textAlign: 'center', marginTop: '100px' }}>
			<h2>Logging you in...</h2>
			<div className='loader' />
		</div>
	);
};

const mapDispatchToProps = (dispatch) => ({
	setPatientId: (data) => dispatch(setPatientId(data)),
});

export default connect(null, mapDispatchToProps)(AutoLogin);
